import React, { useEffect, useState } from 'react'
import ErrorBoundary from '../../../../components/FundDetails/Modules/Performance/ErrorBoundary.jsx'
import * as StringFormatter from '../../../../global/formatters/string-formatter.js'
import DOMPurify from 'dompurify'
import { getRelativeBasePath } from '../../../../global/environments/path.js'
import Loader from '../../../../components/FundDetails/Modules/Shared/Loader.jsx'
import { stickyOnScroll } from '../helpers/InvestmentProductsHelper.js'
import {
  useAllProductTypesReducer,
  filterData,
  getTableHeaderDates,
} from './allProductTypesReducer.js'
import Filters from './AllProductTypesFilters.jsx'
import {
  renderColumnHeader,
  renderColumnCell,
  sortFunds,
} from '../combineMorningstarFunds/combineMorningstarFundsHelper.jsx'
import { removeSpecialCharacters } from '../../../utility/index.js'

function AllProductTypes() {
  const urlParams = new URLSearchParams(window.location.search)
  const [tableHeaderTop, setTableHeaderTop] = useState(0)
  const state = useAllProductTypesReducer()
  const {
    isLoading,
    allProductData,
    selectedSort,
    selectedSortType,
    selectedSortDirection,
    onClickSort,
    selected,
    searchInput,
  } = state

  useEffect(() => {
    stickyOnScroll(setTableHeaderTop)
  }, [])

  const getAspect = () => {
    const _aspects = {
      columns: [
        {
          name: 'fundName',
          className:
            'Table-header u-textNormal sorting bordered' +
            (selectedSort === 'fundName'
              ? selectedSortDirection
                ? ' is-ascending'
                : ' is-descending'
              : ''),
          ariaLabel: 'Fund Name: activate to sort column descending',
          content: <>Fund Name</>,
          button: {
            className:
              'Table-headerNode u-padding2gu u-paddingRight3gu' +
              (selectedSort === 'fundName' ? ' Icon--before' : ''),
            sort: 'fundName',
            sortType: 'string',
          },
          cell: {
            className: ' bordered-right',
            content: (fund) => (
              <div>
                <span className="u-textSecondary u-block">
                  <a
                    href={DOMPurify.sanitize(
                      getRelativeBasePath() +
                        `/investment-products/${fund.productType}/${StringFormatter.urlSafe(removeSpecialCharacters(fund.fundName))}/Class-${
                          fund?.fundClass && fund?.fundClass !== '-'
                            ? fund?.fundClass
                            : 'none'
                        }/details/?cusip=${fund.cusip}`
                    )}
                  >
                    {fund.fundName}
                  </a>
                </span>
                <span>
                  {fund.symbol !== '-' && `${fund.symbol}`}
                  {fund.fundClass !== '-' && fund.symbol !== '-' && ` | `}
                  {fund.fundClass !== '-' && `Class - ${fund.fundClass}`}&nbsp;
                </span>
              </div>
            ),
          },
        },
        {
          name: 'oneYear',
          className:
            'Table-header u-textNormal sorting u-textRight' +
            (selectedSort === 'oneYear'
              ? selectedSortDirection
                ? ' is-ascending'
                : ' is-descending'
              : ''),
          ariaLabel: '1 Year: activate to sort column descending',
          content: <>1 Year</>,
          button: {
            className:
              'Table-headerNode u-paddingVert2gu u-paddingRight1gu u-paddingLeft3gu u-inlineBlock' +
              (selectedSort === 'oneYear' ? ' Icon--before' : ''),
            sort: 'oneYear',
            sortType: 'string',
          },
          cell: {
            className: ' u-textRight',
            content: (fund) => <span>{fund.oneYear}</span>,
          },
        },
        {
          name: 'fiveYear',
          className:
            'Table-header u-textNormal sorting u-textRight ' +
            (selectedSort === 'fiveYear'
              ? selectedSortDirection
                ? ' is-ascending'
                : ' is-descending'
              : ''),
          ariaLabel: '5 Year: activate to sort column descending',
          content: <>5 Year</>,
          button: {
            className:
              'Table-headerNode u-paddingVert2gu u-paddingRight1gu u-paddingLeft3gu u-inlineBlock' +
              (selectedSort === 'fiveYear' ? ' Icon--before' : ''),
            sort: 'fiveYear',
            sortType: 'string',
          },
          cell: {
            className: ' u-textRight',
            content: (fund) => <span>{fund.fiveYear}</span>,
          },
        },
        {
          name: 'tenYear',
          className:
            'Table-header u-textNormal sorting u-textRight ' +
            (selectedSort === 'tenYear'
              ? selectedSortDirection
                ? ' is-ascending'
                : ' is-descending'
              : ''),
          ariaLabel: '10 Year: activate to sort column descending',
          content: <>10 Year</>,
          button: {
            className:
              'Table-headerNode u-paddingVert2gu u-paddingRight1gu  u-paddingLeft3gu u-inlineBlock' +
              (selectedSort === 'tenYear' ? ' Icon--before' : ''),
            sort: 'tenYear',
            sortType: 'string',
          },
          cell: {
            className: ' u-textRight',
            content: (fund) => <span>{fund.tenYear}</span>,
          },
        },
        {
          name: 'sinceInception',
          className:
            'Table-header u-textNormal sorting bordered u-textRight ' +
            (selectedSort === 'sinceInception'
              ? selectedSortDirection
                ? ' is-ascending'
                : ' is-descending'
              : ''),
          ariaLabel: 'Since Fund Incept.: activate to sort column descending',
          content: (
            <>
              Since Fund <br /> Incept.{' '}
            </>
          ),
          button: {
            className:
              'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight u-inlineBlock ' +
              (selectedSort === 'sinceInception' ? ' Icon--before' : ''),
            sort: 'sinceInception',
            sortType: 'string',
          },
          cell: {
            className: ' bordered-right u-textRight',
            content: (fund) => <span>{fund.sinceInception}</span>,
          },
        },
        {
          name: 'grossExp',
          className:
            'Table-header u-textNormal sorting u-textRight' +
            (selectedSort === 'grossExp'
              ? selectedSortDirection
                ? ' is-ascending'
                : ' is-descending'
              : ''),
          ariaLabel: 'Gross: activate to sort column descending',
          content: <>Gross</>,
          button: {
            className:
              'Table-headerNode u-paddingVert2gu u-paddingRight1gu  u-paddingLeft3gu u-inlineBlock ' +
              (selectedSort === 'grossExp' ? ' Icon--before' : ''),
            sort: 'grossExp',
            sortType: 'string',
          },
          cell: {
            className: '  u-textRight',
            content: (fund) => <span>{fund.grossExp}</span>,
          },
        },
        {
          name: 'netExp',
          className:
            'Table-header u-textNormal sorting u-textRight' +
            (selectedSort === 'netExp'
              ? selectedSortDirection
                ? ' is-ascending'
                : ' is-descending'
              : ''),
          ariaLabel: 'Net: activate to sort column descending',
          content: <>Net</>,
          button: {
            className:
              'Table-headerNode u-paddingVert2gu u-paddingRight1gu  u-paddingLeft3gu u-inlineBlock' +
              (selectedSort === 'netExp' ? ' Icon--before' : ''),
            sort: 'netExp',
            sortType: 'string',
          },
          cell: {
            className: '  u-textRight',
            content: (fund) => <span>{fund.netExp}</span>,
          },
        },
        {
          name: 'waiverDate',
          className:
            'Table-header u-textNormal sorting bordered u-textRight' +
            (selectedSort === 'waiverDate'
              ? selectedSortDirection
                ? ' is-ascending'
                : ' is-descending'
              : ''),
          ariaLabel:
            'Waiver Expiration Date: activate to sort column descending',
          content: (
            <>
              Waiver
              <br />
              Expiration
              <br />
              Date
            </>
          ),
          button: {
            className:
              'Table-headerNode u-padding2gu u-paddingLeft3gu u-inlineBlock u-textRight' +
              (selectedSort === 'waiverDate' ? ' Icon--before' : ''),
            sort: 'waiverDate',
            sortType: 'string',
          },
          cell: {
            className: ' bordered-right u-textRight',
            content: (fund) => <span>{fund.waiverDate}</span>,
          },
        },
        {
          name: 'productType',
          attr: { style: { width: '106px' } },
          className:
            'Table-header u-textNormal sorting bordered' +
            (selectedSort === 'productType'
              ? selectedSortDirection
                ? ' is-ascending'
                : ' is-descending'
              : ''),
          ariaLabel: 'Vehicle: activate to sort column descending',
          content: <>Vehicle</>,
          button: {
            className:
              'Table-headerNode u-padding2gu u-paddingRight3gu u-inlineBlock' +
              (selectedSort === 'productType' ? ' Icon--before' : ''),
            sort: 'productType',
            sortType: 'string',
          },
          cell: {
            className: ' bordered-right',
            content: (fund) => <span>{fund?.productLabel}</span>,
          },
        },
        {
          name: 'categoryName',
          attr: { style: { width: '160px' } },
          className:
            'Table-header u-textNormal sorting bordered ' +
            (selectedSort === 'categoryName'
              ? selectedSortDirection
                ? ' is-ascending'
                : ' is-descending'
              : ''),
          ariaLabel: 'Asset Class: activate to sort column descending',
          content: <>Asset Class</>,
          button: {
            className:
              'Table-headerNode u-padding2gu u-paddingRight3gu u-inlineBlock' +
              (selectedSort === 'categoryName' ? ' Icon--before' : ''),
            sort: 'categoryName',
            sortType: 'string',
          },
          cell: {
            className: ' bordered-right',
            content: (fund) => <span>{fund.categoryName}</span>,
          },
        },
        {
          name: 'msCategory',
          attr: { style: { width: '115px' } },
          className:
            'Table-header u-textNormal sorting ' +
            (selectedSort === 'msCategory'
              ? selectedSortDirection
                ? ' is-ascending'
                : ' is-descending'
              : ''),
          ariaLabel: 'Morningstar Category: activate to sort column',
          content: (
            <>
              Morningstar
              <br />
              Category
            </>
          ),
          button: {
            className:
              'Table-headerNode u-textLeft u-padding2gu u-paddingRight3gu u-inlineBlock ' +
              (selectedSort === 'msCategory' ? ' Icon--before' : ''),
            sort: 'msCategory',
            sortType: 'array',
          },
          cell: {
            className: ' u-paddingRight0',
            content: (fund) => fund.msCategory,
          },
        },
      ],
    }

    return _aspects
  }
  const aspect = getAspect()

  const tableRowData = filterData(selected, searchInput, allProductData)

  const tableHeaderDates = getTableHeaderDates(selected, allProductData)

  return (
    <>
      {isLoading && <Loader showBackground={false} />}
      {!isLoading && (
        <>
          <Filters {...state} urlParams={urlParams} />
          <div className="Form-label">
            Displaying{' '}
            <span
              className="u-textBold"
              data-qa-automation="displayingFundCount"
            >
              {tableRowData?.length || 0}
            </span>{' '}
            fund(s)
          </div>
          <table className="Table Table--reversed Table--sortable u-sm-block u-overflowX InvestmentProductTable">
            <thead
              className="Table-head is-sticky"
              style={{
                top: tableHeaderTop,
              }}
            >
              <tr role="row" className="Table-row">
                <th
                  colSpan="1"
                  className="Table-header u-bgColorBrandDark bordered u-paddingBottom0"
                >
                  &nbsp;
                </th>
                <th
                  colSpan="4"
                  className="Table-header u-bgColorBrandDark u-paddingHoriz1gu u-textNoWrap u-paddingBottom0 bordered"
                >
                  Annual Average Total Returns as of {tableHeaderDates.asOfDate}
                </th>
                <th
                  colSpan="3"
                  className="Table-header u-bgColorBrandDark u-paddingHoriz1gu u-textNoWrap u-paddingBottom0 bordered"
                >
                  Expense Ratio as of {tableHeaderDates.expenseAsOfDate}
                </th>
                <th className="Table-header u-bgColorBrandDark u-paddingBottom0 bordered">
                  &nbsp;
                </th>
                <th className="Table-header u-bgColorBrandDark u-paddingBottom0 bordered">
                  &nbsp;
                </th>
                <th className="Table-header u-bgColorBrandDark u-paddingBottom0">
                  &nbsp;
                </th>
              </tr>
              <tr role="row" className="Table-row">
                {aspect.columns.map((column) =>
                  renderColumnHeader(column, selectedSortDirection, onClickSort)
                )}
              </tr>
            </thead>
            {tableRowData.length < 1 ? (
              <tbody className="Table-body Table-row">
                <tr>
                  <td className="Table-cell" colSpan={12}>
                    No matching funds found.
                  </td>
                </tr>
              </tbody>
            ) : (
              sortFunds(
                tableRowData,
                selectedSortDirection,
                selectedSortType,
                selectedSort
              ).map((fund, idx) => {
                return (
                  <tbody
                    className="Table-body Table-row"
                    key={`${fund.fundType}-${idx}`}
                  >
                    <React.Fragment>
                      <tr role="row">
                        {aspect.columns
                          .filter((column) => !column.hide)
                          .map((column) =>
                            renderColumnCell(
                              {
                                ...column,
                                key: `${fund.fundType}-${idx}-${column.name}`,
                              },
                              fund
                            )
                          )}
                      </tr>
                    </React.Fragment>
                  </tbody>
                )
              })
            )}
          </table>
        </>
      )}
    </>
  )
}

export default function AllProductTypesContainer() {
  return (
    <div className="u-sizeConstrained Content investment-products">
      <ErrorBoundary
        title="Find a Product"
        message="Find a Product information is currently unavailable due to a technical issue. Please try again later."
        isConstrained={true}
      >
        <AllProductTypes />
      </ErrorBoundary>
    </div>
  )
}
